import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Copy, GripVertical, Loader2, StickyNote, Trash2 } from 'lucide-react'

const SortableMealItem = ({
  item,
  mealId,
  planId,
  onDelete,
  onNote,
  onNameChange,
  onNameSelect,
  nameSuggestions = [],
  searchLoading,
  searchTerm,
  onNameBlur,
  onQtyChange,
  onQtyBlur,
  onFieldChange,
  onFieldBlur,
  onCopy,
  copying,
  saving,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    data: { type: 'mealItem', mealId, planId },
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }
  const hasNote = Boolean(item.note && String(item.note).trim())

  const nameWrapperRef = useRef(null)
  const [dropdownRect, setDropdownRect] = useState(null)
  const showSuggestions = nameSuggestions.length > 0 && Boolean(searchTerm)

  useEffect(() => {
    if (!showSuggestions) {
      setDropdownRect(null)
      return undefined
    }
    const updateRect = () => {
      const el = nameWrapperRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      setDropdownRect({ top: rect.bottom, left: rect.left, width: rect.width })
    }
    updateRect()
    window.addEventListener('scroll', updateRect, true)
    window.addEventListener('resize', updateRect)
    return () => {
      window.removeEventListener('scroll', updateRect, true)
      window.removeEventListener('resize', updateRect)
    }
  }, [showSuggestions, nameSuggestions, searchTerm])

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`relative transition-colors ${
        isDragging ? 'z-10 bg-white shadow-sm ring-1 ring-fitness-primary/30' : 'hover:bg-slate-50/80'
      }`}
    >
      <td className="px-2 py-2">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="flex h-8 w-8 cursor-grab items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-400 transition hover:border-fitness-primary/40 hover:text-fitness-primary active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </td>
      <td className="px-2 py-2">
        <div className="relative" ref={nameWrapperRef}>
          <input
            type="text"
            value={item.name || ''}
            onChange={(e) => onNameChange?.(item, mealId, planId, e.target.value)}
            onBlur={() => onNameBlur?.(item, mealId, planId)}
            placeholder="Search or type a food name"
            disabled={saving}
            className="w-full min-w-0 rounded-lg border border-slate-200 px-2 py-2 text-xs font-medium text-slate-800 transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          />
          {searchLoading && searchTerm ? (
            <Loader2 className="absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-slate-400" />
          ) : null}
          {showSuggestions && dropdownRect
            ? createPortal(
                <div
                  style={{
                    position: 'fixed',
                    top: dropdownRect.top + 4,
                    left: dropdownRect.left,
                    width: dropdownRect.width,
                  }}
                  className="z-[100] max-h-56 overflow-auto rounded-lg border border-slate-200 bg-white shadow-sm"
                >
                  {nameSuggestions.map((suggestion, index) => {
                    const suggestionName = suggestion.item_name || suggestion.name || 'Food'
                    const brand = suggestion.brand_name
                    const calories = suggestion.calories ?? suggestion.nutrient_value ?? suggestion.nf_calories ?? ''
                    const measure = suggestion.serving_qty && (suggestion.serving_uom || suggestion.serving_unit)
                      ? `${suggestion.serving_qty} ${suggestion.serving_uom || suggestion.serving_unit}`
                      : ''
                    const thumb = suggestion.thumbnail
                    const key = `${item.id}-${index}-${suggestion.resource_id || suggestion.nix_item_id || suggestion.item_name || suggestion.name || 'food'}`
                    return (
                      <button
                        key={key}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => onNameSelect?.(item, mealId, planId, suggestion)}
                        className="flex w-full items-center gap-3 border-b border-slate-100 px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50 last:border-b-0"
                      >
                        {thumb ? <img src={thumb} alt="" className="h-8 w-8 rounded object-cover" /> : <div className="h-8 w-8 rounded bg-slate-100" />}
                        <div className="flex min-w-0 flex-col gap-0.5">
                          <span className="truncate text-[12px] font-semibold text-slate-800">{suggestionName}</span>
                          <div className="flex flex-wrap items-center gap-2 text-[10px] text-slate-500">
                            {brand ? <span className="truncate">Brand: {brand}</span> : null}
                            {measure ? <span>{measure}</span> : null}
                            {calories !== '' ? <span>{calories} calories</span> : null}
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>,
                document.body
              )
            : null}
        </div>
      </td>
      <td className="px-2 py-2">
        <input
          type="number"
          value={item.qty}
          onChange={(e) => onQtyChange?.(item, mealId, planId, e.target.value)}
          onBlur={() => onQtyBlur?.(item, mealId, planId)}
          disabled={saving}
          className="w-full min-w-0 [appearance:textfield] rounded-md border border-slate-300 px-2 py-2 text-center text-[13px] font-medium tabular-nums text-slate-800 transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
      </td>
      <td className="px-2 py-2">
        <input
          type="text"
          value={item.unit || ''}
          onChange={(e) => onFieldChange?.(item, mealId, planId, 'unit', e.target.value)}
          onBlur={() => onFieldBlur?.(item, mealId, planId, 'unit')}
          disabled={saving}
          className="w-full min-w-0 rounded-md border border-slate-300 px-2 py-2 text-center text-[13px] font-medium text-slate-600 transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
        />
      </td>
      <td className="px-2 py-2">
        <input
          type="number"
          value={item.calories}
          onChange={(e) => onFieldChange?.(item, mealId, planId, 'calories', e.target.value)}
          onBlur={() => onFieldBlur?.(item, mealId, planId, 'calories')}
          disabled={saving}
          className="w-full min-w-0 [appearance:textfield] rounded-md border border-slate-300 px-2 py-2 text-center text-[13px] font-semibold tabular-nums text-slate-700 transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
      </td>
      <td className="px-2 py-2">
        <input
          type="number"
          value={item.protein}
          onChange={(e) => onFieldChange?.(item, mealId, planId, 'protein', e.target.value)}
          onBlur={() => onFieldBlur?.(item, mealId, planId, 'protein')}
          disabled={saving}
          className="w-full min-w-0 [appearance:textfield] rounded-md border border-slate-300 px-2 py-2 text-center text-[13px] font-medium tabular-nums text-slate-500 transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
      </td>
      <td className="px-2 py-2">
        <input
          type="number"
          value={item.carbs}
          onChange={(e) => onFieldChange?.(item, mealId, planId, 'carbs', e.target.value)}
          onBlur={() => onFieldBlur?.(item, mealId, planId, 'carbs')}
          disabled={saving}
          className="w-full min-w-0 [appearance:textfield] rounded-md border border-slate-300 px-2 py-2 text-center text-[13px] font-medium tabular-nums text-slate-500 transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
      </td>
      <td className="px-2 py-2">
        <input
          type="number"
          value={item.fat}
          onChange={(e) => onFieldChange?.(item, mealId, planId, 'fat', e.target.value)}
          onBlur={() => onFieldBlur?.(item, mealId, planId, 'fat')}
          disabled={saving}
          className="w-full min-w-0 [appearance:textfield] rounded-md border border-slate-300 px-2 py-2 text-center text-[13px] font-medium tabular-nums text-slate-500 transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
      </td>
      <td className="px-2 py-2">
        <div className="flex items-center justify-end gap-1 text-slate-400">
          <button
            type="button"
            onClick={() => onCopy?.(item, mealId, planId)}
            disabled={copying || saving}
            className="rounded-lg border border-slate-200 bg-white p-1.5 transition hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            title="Copy item"
          >
            {copying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
          <button
            type="button"
            onClick={() => onNote?.(item, mealId, planId)}
            className={`rounded-lg border bg-white p-1.5 transition hover:border-slate-300 hover:text-slate-800 ${
              hasNote ? 'border-fitness-primary/40 text-fitness-primary' : 'border-slate-200'
            }`}
            title={hasNote ? 'Edit note' : 'Add note'}
          >
            <StickyNote className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onDelete?.(item, mealId, planId)}
            className="rounded-lg border border-red-200 bg-white p-1.5 text-red-500 transition hover:border-red-300 hover:text-red-600"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </td>
    </tr>
  )
}

export default SortableMealItem
